import { useEffect, useRef, useState } from 'react';
import './Subscriptions.scss';
import './Landing.scss';
import './ILMDLanding.scss';
import '../components/ui/Form.scss';
import * as Yup from "yup";
import Heading from '../components/ui/Heading';
import Button from '../components/ui/Button';
import "swiper/css/pagination";
import { useDispatch, useSelector } from 'react-redux';
import { resetCustomizer, selectUploadedImageIds, setCustomizerStep } from '../store/customizerSlice';
import { deleteImageFromDB, getImageFromDB, resetIndexedDB, saveImageToDB } from '../utils/indexedDB';
import { resetOrderDetails } from '../store/checkoutSlice';
import { useAffiliate } from '../hooks/useAffiliate';
import { Oval } from 'react-loader-spinner';
import { scrollToId } from '../utils/scrollToId';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import ilmd from '/affiliates/ILMD-logo.png';
import PetCustomizer from '../components/customizer/PetCustomizer';
import IlmdProductSelect from '../components/product/IlmdProductSelect';
import { trackCustomEvent } from '../utils/eventsTracking/trackCustomEvent';


function ILMDLanding({ isMobile }) {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.cartItems);
  const { affiliate, isLoading } = useAffiliate();
  const images = useSelector((state) => state.customizer.images);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const formikRef = useRef(null);

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email address").required("E-mail is required"),
    dog_name: Yup.string().required('Dog name is required'),
    about_dog: Yup.string().max(2000, 'Maximum 2000 characters allowed')
  });

  const initialValues = {
    email: '',
    dog_name: '',
    about_dog: '',
    join_newsletter: true
  };

  //Reset customizer states
  const resetStates = () => {
    localStorage.removeItem("petType");
    localStorage.removeItem("petName");
    localStorage.removeItem("imageMetadata");
    localStorage.removeItem("label_template");
    resetIndexedDB("uploadedImages", cartItems);
    resetIndexedDB("resultImages", cartItems);
    resetIndexedDB("onProductImages", cartItems);
    dispatch(setCustomizerStep(1));
    dispatch(resetOrderDetails());
    dispatch(resetCustomizer());
  };
  useEffect(() => {
    resetStates();
  }, []);


  const handleSubmit = async (values, { resetForm, setSubmitting }, resultImageBlob = null) => {
    setSubmitStatus(null);
    setSubmitting(true);

    try {
      if (!uploadedFile) {
        setSubmitStatus({ success: false, message: "Please upload a photo before submitting." });
        return;
      }

      const formData = new FormData();
      formData.append("email", values.email);
      formData.append("dog_name", values.dog_name);
      formData.append("dog_photo", uploadedFile);
      formData.append("about_dog", values.about_dog);
      formData.append("join_newsletter", values.join_newsletter ? "true" : "false");

      if (resultImageBlob) {
        const slug = `ilmd-${Date.now()}-${crypto.randomUUID()}`;
        const file = new File([resultImageBlob], `${slug}.png`, { type: "image/png" });
        formData.append("result_image", file);
      }

      const response = await fetch("https://api.treatink.com/functions/v1/ilmd-campaign", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      if (!response.ok) {
        setSubmitStatus({ success: false, message: result.error || "Something went wrong. Please try again." });
        return;
      }

      setSubmitStatus({ success: true, message: "Entry Submitted" });
      resetForm();
      setCurrentStep(2);
      scrollToId("apply-form");
      setSubmitting(false);

      trackCustomEvent('campaign_form_submit', {
        campaign: 'ILMD',
      }, affiliate?.code || null);

    } catch (error) {
      setSubmitStatus({ success: false, message: error.message || "Unexpected error. Try again later." });
    } finally {
      setSubmitting(false);
    }
  };


  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <Oval
          visible={true}
          height={50}
          width={50}
          color="#a99cdf"
          strokeWidth="3"
          strokeWidthSecondary="3"
          secondaryColor="#e2e6ff"
          ariaLabel="oval-loading"
        />
      </div>
    );
  }


  return (
    <div className='ilmd-page landing-page'>
      <div className='home-hero full-width'>
        <div className='flex-container contained-width items-center pb-[200px] md:pb-[100px]'>
          <div className={`flex flex-col items-center md:items-start gap-3 ${!affiliate ? 'md:gap-5' : ''} mt-0 md:mt-0`}>
            <h2 className='text-[28px] leading-[35px] md:text-[45px] md:leading-[55px] text-center md:text-left'>Enter Your Pet for a Chance to Be Featured as Dog of the Day!</h2>
            <Button
              type="button"
              variant="filled"
              style={{ maxWidth: 'fit-content' }}
              onClick={() => {
                scrollToId('apply-form');
              }}
            >
              Get Started
            </Button>

          </div>
          <div className='flex items-center justify-center  md:justify-end '>
            <img
              src={ilmd}
              alt="logo"
              className='p-10 w-full max-w-[500px]'
            />
          </div>
        </div>
      </div>


      <section className="text-container full-width flex justify-center">
        <div className="contained-width flex flex-col gap-5 items-center text-center">
          <h5 className='color-secondary max-w-[630px]'>
            Enter Your Best Friend's Information Below for a Chance to Become Dog of the Day!
          </h5>
          <p className='max-w-[500px]'>
            We have over <b>5.4 million dog lovers</b> and your pup could be our next star!
            Please submit your dog's information below to enter.
          </p>
        </div>
      </section>


      <section className="apply-now-container full-width flex justify-center -mt-[0px] md:-mt-[60px]" id="apply-form">
        <div className="contained-width flex flex-col gap-10 items-center">
          <Heading
            h4Text=""
            h2Text="Dog of the Day Application"
            h2Size="small"
            align="center"
          />

          <div className="apply-now-inner rounded-20 p-12 flex flex-col gap-10 items-center">
            <div className={`${currentStep === 1 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full fade-step`}>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {(formikProps) => {
                  formikRef.current = formikProps;
                  const {
                    values,
                    isValid,
                    dirty,
                    isSubmitting,
                  } = formikProps;

                  return (
                    <Form className="form-container flex gap-5 items-center" >

                      <div className='flex flex-col md:flex-row gap-4 w-full'>
                        <div className="form-group w-full">
                          <label htmlFor="dog_name" className="text-mid color-swap">Dog Name*</label>
                          <Field type="text" name="dog_name" placeholder="Dog Name" className="input-field" />
                          <ErrorMessage name="dog_name" component="div" className="error" />
                        </div>
                      </div>

                      <div className="form-group w-full">
                        <label htmlFor="email" className="text-mid color-swap">Your E-mail*</label>
                        <Field type="email" name="email" id="email" placeholder="Your E-mail" className="input-field" />
                        <ErrorMessage name="email" component="div" className="error" />
                      </div>

                      <div className="form-group w-full">
                        <label htmlFor="about_dog" className="text-mid ">Share Something Special About Your Best Friend (Optional)</label>
                        <Field
                          as="textarea"
                          maxLength={2000}
                          placeholder="Feel free to share something here"
                          name="about_dog"
                          className="input-field h-28 resize-none" />
                      </div>

                      <div className="w-full flex justify-center items-center gap-2 mb-10">
                        <Field type="checkbox" name="join_newsletter" id="join_newsletter" />
                        <label htmlFor="join_newsletter" className="text-mid">
                          Join our newsletter
                        </label>
                      </div>

                      <label className="text-mid color-swap">Upload and position your pet's photo below to finalize your submission!</label>
                      <PetCustomizer
                        ilmdCampaignPage={true}
                        setIlmdCampaignPageStep={setCurrentStep}
                        onSubmit={async (blob) => {
                          if (!formikRef.current) return;
                          const { values, resetForm, setSubmitting } = formikRef.current;
                          await handleSubmit(values, { resetForm, setSubmitting }, blob);
                        }}
                        isMobile={isMobile}
                        submitDisabled={!isValid || !dirty || !images || isSubmitting}
                        isSubmitting={isSubmitting}
                        useFrame="ilmd"
                        setUploadedFile={setUploadedFile}
                        customPetName={values.dog_name}
                      />


                    </Form>
                  )
                }}
              </Formik>
              {submitStatus?.success === false && (
                <div className="status-message text-center mt-2 text-red-500">
                  {submitStatus.message}
                </div>
              )}
            </div>


            <div className={`${currentStep === 2 ? '' : 'hidden'} flex flex-col gap-10 items-center w-full`}>
              {submitStatus?.success === true &&
                <div className='text-center py-5 md:py-10 px-3 max-w-[500px] flex flex-col gap-5'>
                  <h4 className="color-primary font-medium">
                    {submitStatus.message}
                  </h4>
                  <p className='mb-0'>
                    Check out our products – your pet is already featured on them! You can personalize and edit the label – we offer a variety of templates for every occasion.
                  </p>
                </div>
              }
              <div className='slider-products w-full'>
                {currentStep === 2 &&
                  <IlmdProductSelect isMobile={isMobile} />
                }
              </div>
            </div>


          </div>
        </div>
      </section>
    </div>
  );
}

export default ILMDLanding;
